<template>
  <div class='infrastructure-hosts-ports-row'>
    <div class='flag' v-if='hasGeoIndicator'>
      <country-flag
        :country='geoIndicator.country_code'
        size='small'
      />
    </div>
    <div v-else class='flag-pl' />
    <div class='host'>
      <v-badge
        tile
        overlap
        :icon="badgeIcon"
        :content="badgeContent"
        class="manual-config-badge"
        :class="{'copied-badge': displayedCopiedBadge}"
        :offset-x='5'
        :offset-y='7'
        :value='isManual || displayedCopiedBadge'
      >
          <custom-tooltip class="host__tooltip" placement="bottom-start" popper-class="light-tooltip">
            <div slot='buttonContent'>
              <div class='host-text' @click.stop='copyHost(host)'>
                {{ host }}
              </div>
            </div>
            <div slot="content">
              <slot name="tooltip">
                {{ host }}
              </slot>
            </div>
          </custom-tooltip>
      </v-badge>
      <span class='noisy-waf-indication' v-if='behindNoisyWaf'>
        <v-tooltip bottom max-width="380" content-class="noisy-waf-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              color='#e0ad42'
              class="material-icons-outlined noisy-waf-icon"
              v-bind="attrs"
              v-on="on"
            >
              warning
            </v-icon>
          </template>
          <span>{{ $t('infrastructure.host.partial.scan.noisy.waf.tooltip') }}</span>
        </v-tooltip>
      </span>
    </div>
    <div class='popup-icon' @click.stop="showAsnPopup = !showAsnPopup">
      <v-icon class="material-icons-outlined" v-if="hasGeoIndicator">info</v-icon>
    </div>
    <div class='ports' :class='{"show-all": showAllPorts}'>
      <div v-for='(port, indexPort) in portsForDisplay' :key="indexPort">
        <custom-tooltip popper-class="light-tooltip"  v-if='getService(port)'>
          <div slot='buttonContent'>
            <v-chip
              class='port'
              :class="[mapPortColor(port), isClickablePort(port)? 'port__clickable':'']"
              @click.stop='portClickHandler(port)'
            >
              {{ port }}
              <v-icon
                small
                v-if='checkIfPortNew(port)'
                color='#e0ad42'
                class="material-symbols-rounded"
                right
              >
                circle
              </v-icon>
            </v-chip>
          </div>
          <div slot='content'>
            <slot name="tooltip">
              {{getService(port)}}
            </slot>
          </div>
        </custom-tooltip>
        <div v-else>
          <v-chip
            class='port'
            :class="[mapPortColor(port), isClickablePort(port)? 'port__clickable':'']"
            @click.stop='portClickHandler(port)'
          >
            {{ port }}
            <v-icon
              small
              v-if='checkIfPortNew(port)'
              color='#e0ad42'
              class="material-symbols-rounded"
              right
            >
              circle
            </v-icon>
          </v-chip>
        </div>
      </div>
    </div>
    <div class='display-toggle' :class='{ open: showAllPorts }' v-if='ports.length > ((this.windowInnerWidth - 480) / 480)'>
        <span text class='hide-ports-btn' @click.stop="showAllPorts = !showAllPorts">
          {{ captionOfPortsToggleButton }}
        </span>
    </div>
    <asn-popup
      title='host.asn.popup.title'
      :visible-popup='showAsnPopup'
      :showNoButton='false'
      yes-caption='no.selected.employees.popup.ok'
      :data='geoIndicator'
      @clicked='showAsnPopup = false'
      v-if="geoIndicator"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CountryFlag from 'vue-country-flag';
import AsnPopup from '@/components/popup/AsnPopup.vue';
import CustomTooltip from '../common/CustomTooltip.vue';

export default {
  name: 'InfrastructureHostsPortsRow',
  components: {
    CountryFlag,
    CustomTooltip,
    AsnPopup,
  },
  props: {
    website: {
      type: String,
      required: true,
    },
    host: {
      type: String,
      required: true,
    },
    ports: {
      type: Array,
      required: true,
    },
    newPorts: {
      type: Array,
      required: true,
    },
    vulnerablePortsByHost: {
      type: Object,
      required: true,
    },
    isManual: {
      type: Boolean,
      required: true,
    },
    geoIndicator: {
      type: Object,
      required: false,
    },
    behindNoisyWaf: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showAllPorts: false,
      displayedCopiedBadge: false,
      windowInnerWidth: window.innerWidth,
      showAsnPopup: false,
    };
  },
  computed: {
    ...mapGetters([
      'portsToScoreMap',
    ]),
    badgeIcon() {
      return this.displayedCopiedBadge ? '' : 'build';
    },
    badgeContent() {
      return this.displayedCopiedBadge ? this.$t('copied') : '';
    },
    sortPortsBySeverity() {
      const uniqueIntegers = new Set();
      const sortedArray = [];
      if (this.ports && this.ports?.length) {
        this.ports.forEach(item => {
          if (typeof item === 'number') {
            if (!uniqueIntegers.has(item)) {
              sortedArray.push(item.toString());
              uniqueIntegers.add(item);
            }
          } else {
            const intValue = parseInt(item, 10);
            if (!Number.isNaN(intValue) && !uniqueIntegers.has(intValue)) {
              sortedArray.push(item);
              uniqueIntegers.add(intValue);
            }
          }
        });
      }
      const severityOrder = ['critical', 'high', 'medium', 'low', 'informative'];

      sortedArray.sort((a, b) => {
        const severityA = this.portsToScoreMap[a] ? this.portsToScoreMap[a].severity : '';
        const severityB = this.portsToScoreMap[b] ? this.portsToScoreMap[b].severity : '';
        const indexA = severityOrder.indexOf(severityA);
        const indexB = severityOrder.indexOf(severityB);
        if (indexA === indexB || (indexA === 0 && indexB === 1) || (indexB === 0 && indexA === 1)) {
          return Number(a) - Number(b);
        }
        if (severityA === '') {
          return 1;
        }
        if (severityB === '') {
          return -1;
        }
        return indexA - indexB;
      });
      return sortedArray;
    },
    portsForDisplay() {
      return this.showAllPorts ? this.sortPortsBySeverity : this.sortPortsBySeverity.slice(0, (this.windowInnerWidth - 480) / 480);
    },
    captionOfPortsToggleButton() {
      return this.showAllPorts ? this.$t('infrastructure.technologies.hide.list') : this.$t('infrastructure.technologies.show.all.list');
    },
    hasGeoIndicator() {
      return this.geoIndicator !== null;
    },
  },
  created() {
    window.addEventListener('resize', this.windowResizeHandler);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResizeHandler);
  },
  methods: {
    windowResizeHandler() {
      this.windowInnerWidth = window.innerWidth;
    },
    isClickablePort(port) {
      const ports = ['80', '81', '443', '2000', '8000', '8008', '8080', '8443', '8888', '10000', '49152', '9200'];
      return ports.includes(port.toString());
    },
    async copyHost(host) {
      this.$clipboard(host);
      this.displayedCopiedBadge = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge = false;
    },
    mapPortColor(port) {
      if (!this.portsToScoreMap[port]) {
        return '';
      }
      if (this.portsToScoreMap[port].severity === this.$t('informative')) {
        return 'informative';
      }
      if (this.portsToScoreMap[port].severity === this.$t('low')) {
        return 'low';
      }
      if (this.portsToScoreMap[port].severity === this.$t('medium')) {
        return 'medium';
      }
      if (this.portsToScoreMap[port].severity === this.$t('high') || this.portsToScoreMap[port].severity === this.$t('critical')) {
        return 'high';
      }
      return '';
    },
    checkIfPortNew(port) {
      // Check if port is new
      return this.newPorts.includes(port);
    },
    portClickHandler(port) {
      if (this.isClickablePort(port)) {
        let baseUrl = this.website;
        if (this.isManual) {
          baseUrl = this.host;
        }
        if (['80'].includes(port.toString())) {
          window.open(`http://${baseUrl}`, '_blank');
        } else if (['443'].includes(port.toString())) {
          window.open(`https://${baseUrl}`, '_blank');
        } else if (['8000', '8080', '8888', '10000', '2000', '81'].includes(port.toString())) {
          window.open(`http://${baseUrl}:${port}`, '_blank');
        } else if (['8008', '8443', '49152'].includes(port.toString())) {
          window.open(`https://${baseUrl}:${port}`, '_blank');
        } else if (['9200'].includes(port.toString())) {
          window.open(`https://${baseUrl}:${port}/_aliases?pretty=true`, '_blank');
        }
      }
    },
    getService(port) {
      return this.portsToScoreMap[port]?.service || null;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.infrastructure-hosts-ports-row {
  display: flex;
  align-items: center;
  position: relative;
  padding: $space-1 $space-2 0;

  .flag {
    margin-top: rem(-10);
    margin-right: rem(-10);
    margin-left: rem(-12);
  }

  .flag-pl {
    width: rem(30);
  }

  .host {
    width: 30%;
    margin-right: 5%;
    display: flex;
    align-items: center;

    &__tooltip {
      color: var(--text-primary);
      max-width: 100%;
    }

    .manual-config-badge {
      min-width: 0;
      max-width: 100%;

      .host-text {
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:hover {
          cursor: pointer;
        }
      }

      .copied {
        text-transform: capitalize;
        color: var(--brand-primary);
      }
    }

    // Partial-scan (noisy WAF/FW) marker, glued right after the IP.
    .noisy-waf-indication {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      margin-left: $space-1;

      // Informational icon: keep the plain arrow cursor, never a help/question-mark cursor.
      .noisy-waf-icon {
        cursor: default;
        font-size: $font-size-xl;
      }
    }
  }

  .popup-icon {
    width: rem(20);
    margin-right: $space-2;
    margin-left: rem(-20);
    margin-top: rem(-3);
    cursor: pointer;

    .v-icon {
      font-size: $font-size-xl;
      color: var(--text-secondary);
    }
  }

  .display-toggle {
    width: rem(100);
    z-index: 5;
    background: $technology-list-toggle-background;
    position: absolute;
    top: 0;
    right: 0;
    text-align: right;
    margin-top: $space-1;
    padding-left: rem(30);
    justify-content: center;

    .hide-ports-btn {
      color: var(--brand-primary);
      font-family: $default-application-font-regular;
      font-size: $font-size-base;
      letter-spacing: 0;

      &:hover {
        cursor: pointer;
      }
    }

    &.open {
      position: unset;
      background: none;
      padding-left: 0;
    }
  }

  .ports {
    width: 55%;
    display: flex;
    flex-wrap: nowrap;
    justify-content: left;
    position: relative;
    overflow: hidden;

    &.show-all {
      flex-wrap: wrap;
    }

    .port {
      z-index: 5;
      min-width: fit-content;
      max-width: fit-content;
      margin-bottom: $space-0-5;
      background: $issue-severity-low-background-color;
      border: 1.5px solid var(--severity-low);
      color: var(--severity-low);
      text-align: center;
      font-weight: 500;
      font-size: $font-size-base;
      margin-left: $space-1;
      cursor: default;

      &.informative {
        background: $issue-severity-informative-background-color;
        border-color: $issue-severity-informative-color;
        color: $issue-severity-informative-color;
      }

      &.low {
        background: $issue-severity-low-background-color;
        border-color: $issue-severity-low-color;
        color: $issue-severity-low-color;
      }

      &.medium {
        background: $issue-severity-medium-background-color;
        border-color: $issue-severity-medium-color;
        color: $issue-severity-medium-color;
      }

      &.high {
        background: $issue-severity-high-background-color;
        border-color: $issue-severity-high-color;
        color: $issue-severity-high-color;
      }

      &.critical {
        background: $issue-severity-critical-background-color;
        border-color: $issue-severity-critical-color;
        color: $issue-severity-critical-color;
      }

      &__clickable {
        cursor: pointer;
      }
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

// Rendered at body level by Vuetify, so it must live in a non-scoped block.
// The design system standardises all tooltips to $font-size-xs via a doubled-class
// rule (.v-tooltip__content.v-tooltip__content). This is a longer explanatory message,
// so match that specificity pattern to raise just this tooltip to a readable size.
.v-tooltip__content.v-tooltip__content.noisy-waf-tooltip {
  font-size: $font-size-md;
  line-height: 1.5;
  padding: $space-2 $space-3;
  max-width: rem(360);
}

.infrastructure-hosts-ports-row {
  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge {
        background: transparent !important;
        border: none !important;
        color: var(--brand-primary);

        .v-icon {
          font-size: $font-size-md;
          transform: rotate(180deg);
        }
      }
    }

    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge {
          text-transform: capitalize;
          background: transparent !important;
          border: none !important;
          color: var(--brand-primary);
          right: $space-5;
          inset: auto auto calc(100% - 1px) calc(100% - 30px);
        }
      }
    }
  }
}
</style>
